import React, { createContext, useContext, useState } from 'react';

interface DataRefreshContextType {
  // Timestamp counters for different data types
  refreshCounters: {
    jobs: number;
    candidates: number;
    references: number;
  };
  // Methods to trigger refresh for different data types
  refreshJobs: () => void;
  refreshCandidates: () => void;
  refreshReferences: () => void;
  // Method to refresh all data at once
  refreshAll: () => void;
}

const DataRefreshContext = createContext<DataRefreshContextType | undefined>(undefined);

export function DataRefreshProvider({ children }: { children: React.ReactNode }) {
  // Counters to trigger re-fetching of data
  // When incremented, components depending on these values will re-fetch data
  const [jobsCounter, setJobsCounter] = useState<number>(0);
  const [candidatesCounter, setCandidatesCounter] = useState<number>(0);
  const [referencesCounter, setReferencesCounter] = useState<number>(0);

  // Methods to trigger refresh for specific data types
  const refreshJobs = () => setJobsCounter(prev => prev + 1);
  const refreshCandidates = () => setCandidatesCounter(prev => prev + 1);
  const refreshReferences = () => setReferencesCounter(prev => prev + 1);
  
  // Method to refresh all data types at once
  const refreshAll = () => {
    refreshJobs();
    refreshCandidates();
    refreshReferences();
  };

  const contextValue: DataRefreshContextType = {
    refreshCounters: {
      jobs: jobsCounter,
      candidates: candidatesCounter,
      references: referencesCounter,
    },
    refreshJobs,
    refreshCandidates,
    refreshReferences,
    refreshAll,
  };

  return (
    <DataRefreshContext.Provider value={contextValue}>
      {children}
    </DataRefreshContext.Provider>
  );
}

export function useDataRefresh() {
  const context = useContext(DataRefreshContext);
  if (context === undefined) {
    throw new Error('useDataRefresh must be used within a DataRefreshProvider');
  }
  return context;
}